import { UTCDate } from '@date-fns/utc';
import { addDays, format } from 'date-fns';

import type { PostingsDataQuery } from '@revelio/data-access';

import type { PostingChartType } from '../../PageConfigProvider/types/chart';
import type { PostingMetricValues } from '../../PageConfigProvider/types/metric';
import { calculateMeterValue } from '../market-conditions/z-score/calculate-meter-value';
import type { PostingData } from '../PostingDataProvider';
import {
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataSet,
} from '../types';

// Converts API week start date (Monday) to week end date (Sunday) for display.
const addSixDaysToDate = (
  dateString: string | null | undefined
): string | null => {
  if (!dateString) return null;
  try {
    const utcDate = new UTCDate(dateString);
    const offsetDate = addDays(utcDate, 6);
    return format(offsetDate, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Invalid postings date format:', dateString, error);
    return null;
  }
};

const isNotNull = <T>(item: T | null): item is T => item !== null;

export const convertPostingData = ({
  metric,
  data,
  chartType,
  activePrimaryEntityCount = 0,
  selectedPrimaryEntityName,
  selectedPrimaryEntityId,
  exchangeRate = 1,
}: {
  metric: PostingMetricValues;
  activePrimaryEntityCount?: number;
  selectedPrimaryEntityName?: string;
  selectedPrimaryEntityId?: string;
  chartType: PostingChartType;
  data: NonNullable<PostingsDataQuery['posting']>;
  exchangeRate?: number;
}): PostingData => {
  if (
    metric === 'posting_salary' ||
    metric === 'time_to_fill' ||
    metric === 'time_to_fill_percentile' ||
    metric === 'postings_active' ||
    metric === 'postings_new' ||
    metric === 'postings_removed' ||
    (metric === 'postings_volume' &&
      !selectedPrimaryEntityName &&
      !selectedPrimaryEntityId &&
      activePrimaryEntityCount > 1)
  ) {
    const metricKey = (() => {
      switch (metric) {
        case 'posting_salary':
          return 'salary';
        case 'time_to_fill':
        case 'time_to_fill_percentile':
          return 'time_to_fill';
        case 'postings_active':
        case 'postings_volume':
          return 'active';
        case 'postings_new':
          return 'new';
        case 'postings_removed':
          return 'removed';
      }
    })();

    if (
      chartType === 'line' ||
      chartType === 'table' ||
      chartType === 'text' ||
      (chartType === 'volume' && activePrimaryEntityCount > 1)
    ) {
      const postingData: TimeseriesDataSet['data'] = data
        .map((entity): TimeseriesData | null => {
          const metadata = entity?.metadata;
          const dim = metadata?.shortName;
          if (!dim) return null;

          const timeseries: TimeseriesData['timeseries'] =
            entity?.category
              ?.map((timePoint) => {
                const date = addSixDaysToDate(timePoint?.metadata?.shortName);
                if (!date) return null;
                const rawValue = timePoint?.metrics?.[metricKey];
                const value = (() => {
                  if (
                    metric === 'posting_salary' &&
                    typeof rawValue === 'number'
                  ) {
                    return rawValue * exchangeRate;
                  }
                  if (
                    metric === 'time_to_fill_percentile' &&
                    typeof rawValue === 'number'
                  ) {
                    return calculateMeterValue({
                      value: rawValue,
                      metric: 'timeToFill',
                    });
                  }
                  return rawValue ?? null;
                })();
                return { date, value };
              })
              .filter(isNotNull) ?? [];

          return {
            ...(metadata?.id ? { id: String(metadata?.id) } : {}),
            dim,
            timeseries,
          };
        })
        .filter((item) => item !== null);

      return { type: DataType.TIMESERIES, data: postingData };
    }

    if (chartType === 'bar' || chartType === 'gauge') {
      const postingData: SingleDataSet['data'] = data
        .map((entity): SingleDataPoint | null => {
          const metadata = entity?.metadata;
          const dim = metadata?.shortName;
          if (!dim) return null;

          const categories = entity?.category;
          const lastCategory = categories?.[categories.length - 1];
          const rawValue = lastCategory?.metrics?.[metricKey];

          if (typeof rawValue !== 'number') return null;
          const value = (() => {
            if (metric === 'posting_salary') return rawValue * exchangeRate;
            if (metric === 'time_to_fill_percentile') {
              return calculateMeterValue({
                value: rawValue,
                metric: 'timeToFill',
              });
            }
            return rawValue;
          })();
          return {
            ...(metadata?.id ? { id: String(metadata?.id) } : {}),
            dim,
            value,
          };
        })
        .filter((item) => item !== null);

      return { type: DataType.SINGLE, data: postingData };
    }
  }

  // Single-entity override via dropdown even when multiple entities selected
  if (
    metric === 'postings_volume' &&
    (selectedPrimaryEntityName ||
      selectedPrimaryEntityId ||
      activePrimaryEntityCount === 1)
  ) {
    const entity = (() => {
      if (selectedPrimaryEntityId) {
        return data?.find(
          (e) => String(e?.metadata?.id) === selectedPrimaryEntityId
        );
      }
      if (selectedPrimaryEntityName) {
        return data?.find(
          (e) =>
            e?.metadata?.shortName === selectedPrimaryEntityName ||
            e?.metadata?.longName === selectedPrimaryEntityName
        );
      }
      return data?.[0];
    })();

    const activeData: TimeseriesData = {
      dim: 'Active',
      timeseries: [],
      ...(entity?.metadata?.id ? { id: String(entity?.metadata?.id) } : {}),
    };
    const newData: TimeseriesData = {
      dim: 'New',
      timeseries: [],
      ...(entity?.metadata?.id ? { id: String(entity?.metadata?.id) } : {}),
    };
    const removedData: TimeseriesData = {
      dim: 'Removed',
      timeseries: [],
      ...(entity?.metadata?.id ? { id: String(entity?.metadata?.id) } : {}),
    };

    entity?.category?.forEach((timePoint) => {
      const date = addSixDaysToDate(timePoint?.metadata?.shortName);
      if (!date) return;
      const activePostings = timePoint?.metrics?.active;
      const newPostings = timePoint?.metrics?.new;
      const removedPostings = timePoint?.metrics?.removed;
      activeData.timeseries.push({ date, value: activePostings ?? null });
      newData.timeseries.push({ date, value: newPostings ?? null });
      removedData.timeseries.push({ date, value: removedPostings ?? null });
    });

    return {
      type: DataType.TIMESERIES,
      data: [activeData, newData, removedData],
    };
  }

  if (metric === 'postings_volume' && activePrimaryEntityCount > 1) {
    const metricKey = 'active';

    const seriesData: TimeseriesDataSet['data'] = data
      .map((entity): TimeseriesData | null => {
        const metadata = entity?.metadata;
        const dim = metadata?.shortName;
        if (!dim) return null;

        const timeseries: TimeseriesData['timeseries'] =
          entity?.category
            ?.map((timePoint) => {
              const date = addSixDaysToDate(timePoint?.metadata?.shortName);
              if (!date) return null;
              const value = timePoint?.metrics?.[metricKey];
              return { date, value: value ?? null };
            })
            .filter(isNotNull) ?? [];

        return {
          ...(metadata?.id ? { id: String(metadata?.id) } : {}),
          dim,
          timeseries,
        };
      })
      .filter((item) => item !== null);

    return { type: DataType.TIMESERIES, data: seriesData };
  }

  return { type: DataType.TIMESERIES, data: [] };
};
